import type {m_taxonomy} from "~/services/classes/TaxonomyClasses";
import type {KeyedEdit} from "~/prototypes/pages/taxonomy-canvas/taxonomyEdits.ts";
import {applyEdits, countDescendants} from "~/prototypes/pages/taxonomy-canvas/taxonomyEdits.ts";

export type ChangeKind = 'renamed' | 'moved' | 'merged' | 'deleted';

export const CHANGE_KIND_META: Record<ChangeKind, {label: string, color: 'warning' | 'secondary' | 'info' | 'error'}> = {
    renamed: {label: 'Renamed', color: 'warning'},
    moved: {label: 'Moved', color: 'secondary'},
    merged: {label: 'Merged', color: 'info'},
    deleted: {label: 'Deleted', color: 'error'},
};

/** One pending change with the taxonomy just before and just after it */
export type ChangeStep = {
    edit: KeyedEdit
    kind: ChangeKind
    /** Category the change is about (its name before the change) */
    category: string
    summary: string
    before: m_taxonomy.Tree
    after: m_taxonomy.Tree
    /** Categories to highlight */
    focusIds: number[]
    /** Extra context to show (e.g. the old and the new parent of a move) */
    anchorIds: number[]
    /** Small note under a changed category, e.g. `moved from "Indirect Spend"` */
    afterNotes: ReadonlyMap<number, string>
    /** False when an earlier change was reverted and this one no longer applies */
    applies: boolean
}

type Entry = {node: m_taxonomy.Tree, parent: m_taxonomy.Tree | null}

/** Every category by id, removed ones included */
export function indexTree(root: m_taxonomy.Tree): Map<number, Entry> {
    const map = new Map<number, Entry>();
    const visit = (node: m_taxonomy.Tree, parent: m_taxonomy.Tree | null) => {
        map.set(node.id, {node, parent});
        node.children.forEach(c => visit(c, node));
    };
    visit(root, null);
    return map;
}

/** Labels from L1 down to the category (the root is left out) */
function pathLabel(index: Map<number, Entry>, id: number | undefined): string {
    const labels: string[] = [];
    for (let e = id === undefined ? undefined : index.get(id); e?.parent; e = index.get(e.parent.id)) labels.unshift(e.node.label);
    return labels.length ? labels.join(' › ') : 'Taxonomy root';
}

function describe(edit: KeyedEdit, before: m_taxonomy.Tree): Omit<ChangeStep, 'edit' | 'before' | 'after'> {
    const b = indexTree(before);
    const live = (id: number) => {
        const e = b.get(id);
        return e && !e.node.removed ? e : undefined;
    };
    const notApplying = (kind: ChangeKind, category: string): Omit<ChangeStep, 'edit' | 'before' | 'after'> => ({
        kind, category, summary: 'No longer applies: an earlier change it depends on was reverted',
        focusIds: [], anchorIds: [], afterNotes: new Map(), applies: false,
    });
    switch (edit.type) {
        case 'rename': {
            const e = live(edit.id);
            if (!e) return notApplying('renamed', edit.label);
            return {
                kind: 'renamed', category: e.node.label, summary: `${e.node.label} → ${edit.label}`,
                focusIds: [edit.id], anchorIds: e.parent ? [e.parent.id] : [],
                afterNotes: new Map(), applies: true,
            };
        }
        case 'move': {
            const e = live(edit.id);
            const newParent = live(edit.parentId);
            if (!e || !newParent) return notApplying('moved', e?.node.label ?? '');
            return {
                kind: 'moved', category: e.node.label,
                summary: `${pathLabel(b, e.parent?.id)}  →  ${pathLabel(b, edit.parentId)}`,
                focusIds: [edit.id], anchorIds: [...(e.parent ? [e.parent.id] : []), edit.parentId],
                afterNotes: new Map([[edit.id, `moved from "${e.parent?.label ?? ''}"`]]), applies: true,
            };
        }
        case 'merge': {
            const source = live(edit.sourceId);
            const target = live(edit.targetId);
            if (!source || !target) return notApplying('merged', source?.node.label ?? target?.node.label ?? '');
            const name = edit.name ?? target.node.label;
            const n = source.node.children.filter(c => !c.removed).length;
            return {
                kind: 'merged', category: `${source.node.label} + ${target.node.label}`,
                summary: `${source.node.label} + ${target.node.label} → ${name}`
                    + (n > 0 ? ` (${n} sub-categor${n === 1 ? 'y' : 'ies'} moved along)` : ''),
                focusIds: [edit.sourceId, edit.targetId],
                anchorIds: [source.parent, target.parent].filter(p => p !== null).map(p => p.id),
                afterNotes: new Map([
                    [edit.targetId, `merged from "${source.node.label}"`],
                    [edit.sourceId, `merged into "${name}"`],
                ]),
                applies: true,
            };
        }
        case 'delete': {
            const e = live(edit.id);
            if (!e) return notApplying('deleted', '');
            const n = countDescendants(e.node);
            return {
                kind: 'deleted', category: e.node.label,
                summary: n > 0 ? `${e.node.label} and its ${n} sub-categor${n === 1 ? 'y' : 'ies'}` : e.node.label,
                focusIds: [edit.id], anchorIds: e.parent ? [e.parent.id] : [],
                afterNotes: new Map([[edit.id, 'deleted']]), applies: true,
            };
        }
    }
}

/**
 * Replays the edits one by one: approved edits are the baseline, every pending edit becomes a step with
 * the tree before and after it. The root gets the taxonomy name, so a taxonomy rename reads naturally.
 */
export function computeChangeSteps(
    source: m_taxonomy.Tree,
    taxonomyName: string,
    approved: readonly KeyedEdit[],
    pending: readonly KeyedEdit[],
): ChangeStep[] {
    const named = {...source, label: taxonomyName};
    let before = applyEdits(named, approved);
    return pending.map(edit => {
        const after = applyEdits(before, [edit]);
        const step = {edit, before, after, ...describe(edit, before)};
        before = after;
        return step;
    });
}
